import { useState } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  Select,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { usePatients } from '@/features/patients/api';
import {
  teleApi,
  useTeleAction,
  useTeleconsults,
  type Teleconsult,
  type TeleType,
} from '@/features/teleconsults/api';
import { PageContainer } from '@/layouts/PageContainer';
import { whenLabel } from '@/pages/calendar/time';
import { clinicDayLabel } from '@/pages/ops/mc-format';
import styles from '@/pages/ops/parity.module.css';
import { FormDialog } from './FormDialog';
import { CLINIC_TZ } from '@/lib/clinic-time';

const TYPE_LABEL: Record<TeleType, string> = {
  follow_up: 'Follow-up · existing patient',
  first_visit: 'First visit by video',
};
const clinicToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: CLINIC_TZ }).format(new Date());
const money = (sen: number) => `RM ${(sen / 100).toFixed(2)}`;

function StatusBadge({ s }: { s: Teleconsult }) {
  switch (s.status) {
    case 'closed':
      return <Badge tone="success">Closed</Badge>;
    case 'converted':
      return <Badge tone="info">Moved to in-person</Badge>;
    case 'in_progress':
      return <Badge tone="warning">In progress</Badge>;
    default:
      return s.type === 'first_visit' ? (
        <Badge tone="warning">Reason required</Badge>
      ) : (
        <Badge tone="neutral">Scheduled</Badge>
      );
  }
}

type Open =
  | { kind: 'add' }
  | { kind: 'room'; s: Teleconsult }
  | { kind: 'approve' }
  | null;

/**
 * Teleconsults (Admin v2): the day's video queue and the MMC checklist. No
 * video provider is connected, so the room records the identity check,
 * notes, the first-visit reason or conversion to in-person.
 */
export function TeleconsultsPage() {
  const { hasPermission } = useSession();
  const canManage = hasPermission('teleconsults:manage');
  const [date, setDate] = useState(clinicToday);
  const [open, setOpen] = useState<Open>(null);
  const q = useTeleconsults(date);
  const cfg = q.data?.config;
  return (
    <PageContainer>
      {cfg && !cfg.approved && (
        <div className={styles.notice} role="status">
          <strong>The checklist is pending clinical sign-off.</strong>{' '}
          {cfg.source}
          {canManage && (
            <>
              {' '}
              <Button size="sm" onClick={() => setOpen({ kind: 'approve' })}>
                Record sign-off
              </Button>
            </>
          )}
        </div>
      )}
      {cfg && !cfg.videoProvider && (
        <p className={styles.infoNotice}>
          No video provider is connected yet. Slots, the identity check, notes
          and outcomes are recorded here; the call itself happens outside the
          system.
        </p>
      )}
      <div className={styles.grid3}>
        <Card
          title="Teleconsult queue"
          flush
          actions={
            <div className={styles.rowActions}>
              <Input
                type="date"
                aria-label="Day"
                className={styles.headerControl}
                value={date}
                onChange={(e) => setDate(e.target.value || clinicToday())}
              />
              {canManage && (
                <Button size="sm" onClick={() => setOpen({ kind: 'add' })}>
                  Add slot
                </Button>
              )}
            </div>
          }
        >
          <DataTable
            caption="Teleconsult queue"
            rows={q.data?.rows}
            rowKey={(s) => s.id}
            loading={q.isLoading}
            error={q.error}
            onRetry={() => void q.refetch()}
            empty={<EmptyState title="No teleconsults on this day" />}
            columns={[
              {
                key: 't',
                header: 'Time',
                render: (s) => whenLabel(s.startsAt),
              },
              {
                key: 'p',
                header: 'Patient',
                render: (s) => (
                  <Link to={`/patients/${s.patient.id}`}>
                    {s.patient.fullName}
                  </Link>
                ),
              },
              {
                key: 'k',
                header: 'Type',
                render: (s) => (
                  <>
                    {TYPE_LABEL[s.type]}
                    <span className={styles.sub}>{s.doctor.fullName}</span>
                  </>
                ),
              },
              {
                key: 'f',
                header: 'Paid',
                render: (s) => (
                  <Badge tone="success">{money(s.feePaidSen)} recorded</Badge>
                ),
              },
              {
                key: 's',
                header: 'Status',
                render: (s) => <StatusBadge s={s} />,
              },
              {
                key: 'x',
                header: '',
                render: (s) =>
                  canManage &&
                  s.mine &&
                  ['booked', 'in_progress'].includes(s.status) ? (
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => setOpen({ kind: 'room', s })}
                    >
                      {s.status === 'booked' ? 'Start' : 'Continue'}
                    </Button>
                  ) : s.note ? (
                    <span className={styles.sub}>{s.note.split('\n')[0]}</span>
                  ) : null,
              },
            ]}
          />
        </Card>
        <Card title="MMC checklist for every call">
          <ol className={styles.form} style={{ margin: 0, paddingLeft: 18 }}>
            {cfg?.checklist.map((c) => (
              <li key={c.key}>
                <strong>{c.title}</strong>
                <span className={styles.sub}>{c.detail}</span>
              </li>
            ))}
          </ol>
        </Card>
      </div>
      {open?.kind === 'add' && <AddSlotDialog onClose={() => setOpen(null)} />}
      {open?.kind === 'room' && (
        <RoomDialog s={open.s} onClose={() => setOpen(null)} />
      )}
      {open?.kind === 'approve' && (
        <ApproveDialog onClose={() => setOpen(null)} />
      )}
    </PageContainer>
  );
}

function AddSlotDialog({ onClose }: { onClose: () => void }) {
  const a = useTeleAction(teleApi.create);
  const [search, setSearch] = useState('');
  const [patientId, setPatientId] = useState('');
  const [when, setWhen] = useState('');
  const [type, setType] = useState<TeleType>('follow_up');
  const [fee, setFee] = useState('30');
  const found = usePatients({ q: search.trim() || undefined, pageSize: 8 });
  const feeSen = Math.round(Number(fee) * 100);
  return (
    <FormDialog
      title="Add teleconsult slot"
      onClose={onClose}
      pending={a.isPending}
      disabled={!patientId || !when || !(feeSen >= 0)}
      submitLabel="Add slot"
      onSubmit={() =>
        a.mutateAsync({
          patientId,
          startsAt: new Date(`${when}:00+08:00`).toISOString(),
          type,
          feePaidSen: feeSen,
        })
      }
    >
      <Field label="Find patient">
        <Input
          value={search}
          placeholder="Name or IC"
          onChange={(e) => setSearch(e.target.value)}
        />
      </Field>
      <Field label="Patient">
        <Select
          value={patientId}
          placeholder="Choose a patient"
          onChange={(e) => setPatientId(e.target.value)}
          options={(found.data?.data ?? []).map((p) => ({
            value: p.id,
            label: p.fullName,
          }))}
        />
      </Field>
      <Field label="Time">
        <Input
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
        />
      </Field>
      <Field label="Type">
        <Select
          value={type}
          onChange={(e) => setType(e.target.value as TeleType)}
          options={(Object.keys(TYPE_LABEL) as TeleType[]).map((t) => ({
            value: t,
            label: TYPE_LABEL[t],
          }))}
        />
      </Field>
      <Field label="Fee paid (RM)">
        <Input
          inputMode="decimal"
          value={fee}
          onChange={(e) => setFee(e.target.value)}
        />
      </Field>
      <p className={styles.sub}>
        The fee is recorded as paid; no payment gateway is connected.
      </p>
    </FormDialog>
  );
}

function RoomDialog({ s, onClose }: { s: Teleconsult; onClose: () => void }) {
  const start = useTeleAction(teleApi.start);
  const close = useTeleAction(teleApi.close);
  const convert = useTeleAction(teleApi.convert);
  const [identity, setIdentity] = useState(false);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [convertWhy, setConvertWhy] = useState('');
  const [mode, setMode] = useState<'close' | 'convert'>('close');
  const started = s.status === 'in_progress';
  if (!started)
    return (
      <FormDialog
        title={`Teleconsult · ${s.patient.fullName}`}
        onClose={onClose}
        pending={start.isPending}
        disabled={!identity}
        submitLabel="Start consult"
        onSubmit={() => start.mutateAsync(s.id)}
      >
        <p className={styles.notice}>
          Identity: compare the patient on camera with the MyKad photo on file,
          then confirm. Recorded with the consult (MMC cl. 11e).
        </p>
        <label className={styles.rowActions}>
          <input
            type="checkbox"
            checked={identity}
            onChange={(e) => setIdentity(e.target.checked)}
          />{' '}
          Identity confirmed on camera
        </label>
      </FormDialog>
    );
  const first = s.type === 'first_visit';
  return mode === 'close' ? (
    <FormDialog
      title={`Teleconsult · ${s.patient.fullName}`}
      onClose={onClose}
      pending={close.isPending}
      disabled={notes.trim().length < 5 || (first && reason.trim().length < 10)}
      submitLabel="Close consult"
      onSubmit={() =>
        close.mutateAsync({
          id: s.id,
          notes: notes.trim(),
          ...(first ? { firstVisitReason: reason.trim() } : {}),
        })
      }
    >
      <Badge tone="success">
        Identity confirmed{' '}
        {s.identityConfirmedAt ? clinicDayLabel(s.identityConfirmedAt) : ''}
      </Badge>
      {first && (
        <Field label="Reason this first visit is by video (MMC cl. 15)">
          <textarea
            className={styles.textarea}
            rows={2}
            maxLength={250}
            value={reason}
            placeholder="e.g. patient is out of state, condition suitable for video review"
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      )}
      <Field label="Notes">
        <textarea
          className={styles.textarea}
          rows={5}
          maxLength={700}
          value={notes}
          placeholder="Presenting complaint, assessment…"
          onChange={(e) => setNotes(e.target.value)}
        />
      </Field>
      <Button size="sm" variant="ghost" onClick={() => setMode('convert')}>
        Medium not adequate? Convert to in-person
      </Button>
    </FormDialog>
  ) : (
    <FormDialog
      title={`Convert to in-person · ${s.patient.fullName}`}
      onClose={onClose}
      pending={convert.isPending}
      disabled={convertWhy.trim().length < 5}
      submitLabel="Convert to in-person"
      onSubmit={() =>
        convert.mutateAsync({ id: s.id, reason: convertWhy.trim() })
      }
    >
      <p className={styles.notice}>
        The fee recorded for this slot ({money(s.feePaidSen)}) is credited at
        the counter for the in-person visit (MMC cl. 12a).
      </p>
      <Field label="Why video is not adequate">
        <Input
          value={convertWhy}
          maxLength={300}
          onChange={(e) => setConvertWhy(e.target.value)}
        />
      </Field>
      <Button size="sm" variant="ghost" onClick={() => setMode('close')}>
        Back
      </Button>
    </FormDialog>
  );
}

function ApproveDialog({ onClose }: { onClose: () => void }) {
  const a = useTeleAction(teleApi.approve);
  const [note, setNote] = useState('');
  return (
    <FormDialog
      title="Record clinical sign-off"
      onClose={onClose}
      pending={a.isPending}
      disabled={note.trim().length < 5}
      submitLabel="Record sign-off"
      onSubmit={() => a.mutateAsync({ note: note.trim() })}
    >
      <p className={styles.notice}>
        Confirms the teleconsult checklist shown on this page.
      </p>
      <Field label="Sign-off note (who approved, reference)">
        <Input
          value={note}
          maxLength={300}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
    </FormDialog>
  );
}
